import React, { useState, useEffect, useCallback } from 'react';
import './AttendanceMessage.css';

/** Получить дату сообщения в формате YYYY-MM-DD (по локальному времени) */
function getMessageDate(message) {
  const d = new Date(message.createdAt);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

/** Сегодняшняя дата в формате YYYY-MM-DD */
function getTodayDate() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function formatDate(dateStr) {
  const [y, m, d] = dateStr.split('-');
  return `${d}.${m}.${y}`;
}

function getPhotoUrl(employee) {
  if (!employee) return null;
  if (employee.chat_avatar) return `/avatars/${employee.chat_avatar}`;
  if (!employee.photo_url) return null;
  if (employee.photo_url.startsWith('http')) return employee.photo_url;
  return `https://zhylytaban.kz/admin${employee.photo_url}`;
}

function EmployeeAvatar({ employee, size = 40 }) {
  const photo = getPhotoUrl(employee);
  const initials = employee
    ? `${(employee.first_name || '')[0] || ''}${(employee.last_name || '')[0] || ''}`
    : '?';
  if (photo) {
    return (
      <img
        src={photo}
        alt={employee?.first_name || ''}
        style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: employee ? 'linear-gradient(135deg, #667eea, #764ba2)' : '#c0c4cc',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontSize: Math.round(size * 0.35), fontWeight: 700,
    }}>
      {initials}
    </div>
  );
}

/** Статичный вид явки за прошедшие дни */
function AttendanceMessageStatic({ message }) {
  const [employees, setEmployees] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const token = localStorage.getItem('token');
  const date = getMessageDate(message);

  useEffect(() => {
    fetch(`/api/duty/by-date?date=${date}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { if (!res.ok) throw new Error('Ошибка загрузки'); return res.json(); })
      .then(data => setEmployees(data.employees || []))
      .catch(err => setLoadError(err.message));
  }, [date, token]);

  if (loadError) {
    return (
      <div className="att-message att-error">
        <div className="att-title">📋 Явка — {formatDate(date)}</div>
        <div className="att-err-text">Не удалось загрузить данные: {loadError}</div>
      </div>
    );
  }

  if (employees === null) {
    return <div className="att-message"><div className="att-title">📋 Явка — {formatDate(date)}</div></div>;
  }

  return (
    <div className="att-message att-message--static">
      <div className="att-title">📋 Явка — {formatDate(date)}</div>
      {employees.length === 0 ? (
        <div className="att-empty">Никто не отмечен</div>
      ) : (
        <div className="att-marked-list">
          {employees.map(emp => (
            <div key={emp.employee_id} className="att-marked-item">
              <EmployeeAvatar employee={emp} size={36} />
              <div className="att-marked-info">
                <div className="att-name">{emp.last_name} {emp.first_name}</div>
                {(emp.position_worked || emp.position) && <div className="att-position">{emp.position_worked || emp.position}</div>}
              </div>
              <div className="att-checkbox att-checkbox--on">✓</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Активный вид явки — только для сообщения за сегодня */
function AttendanceMessageActive({ message, currentUser }) {
  const [positionsOnDuty, setPositionsOnDuty] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [allPositions, setAllPositions] = useState([]);
  const [empMap, setEmpMap] = useState({});

  // scheduledSelections: { rowKey -> employeeId }
  const [scheduledSelections, setScheduledSelections] = useState({});
  // removedKeys: set удалённых плановых строк
  const [removedKeys, setRemovedKeys] = useState(new Set());
  // extraRows: [{ key, positionName: null, employeeId: null }]
  const [extraRows, setExtraRows] = useState([]);

  const [markedEmployees, setMarkedEmployees] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const token = localStorage.getItem('token');

  const loadData = useCallback(async () => {
    setLoadError(null);
    try {
      const res = await fetch('/api/duty/schedule-today', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Ошибка загрузки расписания');
      const data = await res.json();

      const positions = data.positions_on_duty || [];
      const allEmps = data.all_employees || [];
      const allPos = data.all_positions || [];
      const marked = data.marked_employees || [];

      setPositionsOnDuty(positions);
      setAllEmployees(allEmps);
      setAllPositions(allPos);
      setMarkedEmployees(marked);

      const map = {};
      allEmps.forEach(e => { map[e.id] = e; });
      setEmpMap(map);

      // Инициализируем selects по расписанию
      const selections = {};
      positions.forEach(group => {
        group.employees.forEach((emp, idx) => {
          selections[`${group.position}_${idx}`] = emp.id;
        });
      });
      setScheduledSelections(selections);

      if (marked.length > 0) setSubmitted(true);
    } catch (err) {
      setLoadError(err.message);
    }
  }, [token]);

  useEffect(() => { loadData(); }, [loadData]);

  const getAllSelected = () => {
    const result = [];
    Object.entries(scheduledSelections).forEach(([rowKey, id]) => {
      if (!id || removedKeys.has(rowKey)) return;
      // rowKey формат: "Должность_idx" — берём всё до последнего "_"
      const positionName = rowKey.substring(0, rowKey.lastIndexOf('_'));
      result.push({ id: Number(id), position: positionName });
    });
    extraRows.forEach(row => {
      if (row.employeeId) result.push({ id: Number(row.employeeId), position: row.positionName || null });
    });
    return result;
  };

  const handleSubmit = async () => {
    const emps = getAllSelected();
    if (emps.length === 0) return;
    setSubmitting(true);
    try {
      // Сбросить старые записи
      await fetch('/api/duty/reset-today', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      // Сохранить новые
      const res = await fetch('/api/duty/submit', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ employees: emps }),
      });
      if (!res.ok) throw new Error('Ошибка сохранения');
      setMarkedEmployees(emps.map(e => ({ id: e.id, positionWorked: e.position })));
      setSubmitted(true);
    } catch (err) {
      alert('Ошибка: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async () => {
    await fetch('/api/duty/reset-today', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    }).catch(() => {});
    setSubmitted(false);
    setMarkedEmployees([]);
    setExtraRows([]);
    setRemovedKeys(new Set());
  };

  const addExtraRow = () => {
    setExtraRows(prev => [...prev, { key: `extra_${Date.now()}`, positionName: null, employeeId: null }]);
  };

  const removeExtraRow = (key) => {
    setExtraRows(prev => prev.filter(r => r.key !== key));
  };

  const updateExtraPosition = (key, positionName) => {
    setExtraRows(prev => prev.map(r => r.key === key ? { ...r, positionName: positionName || null, employeeId: null } : r));
  };

  const updateExtra = (key, val) => {
    setExtraRows(prev => prev.map(r => r.key === key ? { ...r, employeeId: val ? Number(val) : null } : r));
  };

  const updateScheduled = (rowKey, val) => {
    setScheduledSelections(prev => ({ ...prev, [rowKey]: val ? Number(val) : null }));
  };

  const removeScheduled = (rowKey) => {
    setRemovedKeys(prev => new Set([...prev, rowKey]));
  };

  if (loadError) {
    return (
      <div className="att-message att-error">
        <div className="att-title">📋 Кто сегодня в приюте?</div>
        <div className="att-err-text">{loadError}</div>
        <button className="att-submit-btn" style={{ marginTop: 8 }} onClick={loadData}>Повторить</button>
      </div>
    );
  }

  const totalCount = getAllSelected().length;

  // Submitted view
  if (submitted) {
    return (
      <div className="att-message">
        <div className="att-title">📋 Кто сегодня в приюте?</div>
        <div className="att-submitted-banner">
          ✅ Явка отмечена — {markedEmployees.length} чел.
          <button className="att-edit-btn" onClick={handleEdit}>Изменить</button>
        </div>
        <div className="att-marked-list">
          {markedEmployees.map(({ id, positionWorked }) => {
            const emp = empMap[id];
            if (!emp) return null;
            return (
              <div key={id} className="att-marked-item">
                <EmployeeAvatar employee={emp} size={36} />
                <div className="att-marked-info">
                  <div className="att-name">{emp.last_name} {emp.first_name}</div>
                  {positionWorked && <div className="att-position">{positionWorked}</div>}
                </div>
                <div className="att-checkbox att-checkbox--on">✓</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Edit view
  return (
    <div className="att-message">
      <div className="att-title">📋 Кто сегодня в приюте?</div>

      {positionsOnDuty.length === 0 && (
        <div className="att-empty">По расписанию сегодня никто не работает</div>
      )}

      {positionsOnDuty.map(group => {
        const visibleRows = group.employees.filter((_, idx) => !removedKeys.has(`${group.position}_${idx}`));
        if (visibleRows.length === 0) return null;
        return (
          <div key={group.position} className="att-position-group">
            <div className="att-position-header">{group.position}</div>
            {group.employees.map((emp, idx) => {
              const rowKey = `${group.position}_${idx}`;
              if (removedKeys.has(rowKey)) return null;
              const selectedId = scheduledSelections[rowKey];
              const selectedEmp = selectedId ? empMap[selectedId] : null;
              return (
                <div key={rowKey} className="att-select-row">
                  <EmployeeAvatar employee={selectedEmp} size={38} />
                  <select
                    className="att-select"
                    value={selectedId || ''}
                    onChange={e => updateScheduled(rowKey, e.target.value)}
                  >
                    {allEmployees.map(e => (
                      <option key={e.id} value={e.id}>
                        {e.last_name} {e.first_name}
                      </option>
                    ))}
                  </select>
                  <button className="att-row-delete" onClick={() => removeScheduled(rowKey)}>✕</button>
                </div>
              );
            })}
          </div>
        );
      })}

      {extraRows.map(row => {
        
        const selectedEmp = row.employeeId ? empMap[row.employeeId] : null;
        return (
          <div key={row.key} className="att-position-group">
            <div className="att-position-header att-position-header--extra">
              <select
                className="att-position-header-select"
                value={row.positionName || ''}
                onChange={e => updateExtraPosition(row.key, e.target.value)}
              >
                <option value="">— выберите должность —</option>
                {allPositions.map(pos => (
                  <option key={pos.id || pos} value={pos.name || pos}>{pos.name || pos}</option>
                ))}
              </select>
              <button className="att-row-delete" onClick={() => removeExtraRow(row.key)}>✕</button>
            </div>
            {row.positionName && (
              <div className="att-select-row">
                <EmployeeAvatar employee={selectedEmp} size={38} />
                <select
                  className="att-select"
                  value={row.employeeId || ''}
                  onChange={e => updateExtra(row.key, e.target.value)}
                >
                  <option value="">— выберите сотрудника —</option>
                  {allEmployees.map(e => (
                    <option key={e.id} value={e.id}>
                      {e.last_name} {e.first_name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        );
      })}

      <button className="att-add-btn" onClick={addExtraRow}>
        + Добавить сотрудника
      </button>

      <button
        className="att-submit-btn"
        onClick={handleSubmit}
        disabled={submitting || totalCount === 0}
      >
        {submitting ? 'Сохраняем...' : `Подтвердить (${totalCount})`}
      </button>
    </div>
  );
}

/** Точка входа: маршрутизирует на статичный или активный вид */
function AttendanceMessage({ message, currentUser }) {
  const isToday = getMessageDate(message) === getTodayDate();
  if (!isToday) return <AttendanceMessageStatic message={message} />;
  return <AttendanceMessageActive message={message} currentUser={currentUser} />;
}

export default AttendanceMessage;
